import React from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuCheckCheck, LuPhone, LuVideo } from 'react-icons/lu';
import { GLASS_POPOVER } from '../ui/glass';
import { formatMissedCallTime } from '../../utils/missedCalls';
import { normalizeStoredName } from '../../utils/userIdentity';
import { useTranslation } from '../../i18n/LanguageContext';

const actionClass = [
  'tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-transparent tw-transition-colors',
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
].join(' ');
const callBackClass = `${actionClass} tw-text-teal-700 hover:tw-bg-teal-500/10 dark:tw-text-teal-300`;

/** The dropdown list of the top bar's missed-call button. */
const MissedCallsPanel = ({ missedCalls, onCallBack, onDismiss, onDismissAll }) => {
  const { t, locale } = useTranslation();

  return (
    <div className={`tw-overflow-hidden tw-rounded-2xl ${GLASS_POPOVER}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-slate-200/70 tw-px-4 tw-py-3 dark:tw-border-white/10">
        <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
          {t('missedCalls.title')} <span className="tw-text-rose-600 dark:tw-text-rose-300">({missedCalls.length})</span>
        </p>
        <button
          type="button"
          onClick={onDismissAll}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 dark:tw-text-teal-300"
        >
          <LuCheckCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {t('missedCalls.markAllRead')}
        </button>
      </div>

      <ul className="tw-max-h-80 tw-divide-y tw-divide-slate-200/70 tw-overflow-y-auto glass-scrollbar dark:tw-divide-white/10">
        {missedCalls.map((room) => {
          const isVoice = (room.callType || room.type) === 'voice';
          const TypeIcon = isVoice ? LuPhone : LuVideo;
          return (
            <li key={room.id} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
              <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300">
                <TypeIcon aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
                  {normalizeStoredName(room.createdByName, room.createdBy)}
                </p>
                {room.createdBy && (
                  <p className="tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{room.createdBy}</p>
                )}
                <p className="tw-text-xs tw-text-rose-600 dark:tw-text-rose-300">
                  {isVoice ? t('missedCalls.missedVoice') : t('missedCalls.missedVideo')}
                  {' · '}
                  {formatMissedCallTime(room.missedAt || room.createdAt, locale)}
                </p>
              </div>
              <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                <button
                  type="button"
                  onClick={() => onCallBack(room, 'voice')}
                  title={t('missedCalls.callBackVoice')}
                  aria-label={t('missedCalls.callBackVoice')}
                  className={callBackClass}
                >
                  <LuPhone aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onCallBack(room, 'video')}
                  title={t('missedCalls.callBackVideo')}
                  aria-label={t('missedCalls.callBackVideo')}
                  className={callBackClass}
                >
                  <LuVideo aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onDismiss(room)}
                  title={t('missedCalls.markRead')}
                  aria-label={t('missedCalls.markRead')}
                  className={`${actionClass} tw-text-slate-400 hover:tw-bg-slate-900/[0.05] hover:tw-text-slate-700 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white`}
                >
                  <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

MissedCallsPanel.propTypes = {
  missedCalls: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCallBack: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onDismissAll: PropTypes.func.isRequired,
};

export default MissedCallsPanel;
